import { useEffect, useState } from "react"

import { ColumnDef, ColumnFiltersState, flexRender, getCoreRowModel, getFilteredRowModel, getSortedRowModel, SortingState, useReactTable, VisibilityState } from "@tanstack/react-table"
import { AccountRef, ContentObjectItem, ProjectRef } from "@vertesia/common"
import { MoreHorizontal } from "lucide-react"

import { Button } from "@ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "@ui/dropdown-menu"
import { Input } from "@ui/input"
import { Slider } from "@ui/slider"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ui/table"
import { useUserSession } from "@session/UserSession"

function columns(account: AccountRef | undefined, project: ProjectRef | undefined): ColumnDef<ContentObjectItem>[] {
    return [
        {
            accessorKey: "name",
            header: "Name",
            cell: ({ row }) => {
                const properties = (row.original as any)?.properties
                return (
                    <div>{properties?.title || row.getValue("name") || row.getValue("id")}</div>
                )
            }
        },
        {
            accessorKey: "score",
            header: "Score",
            cell: ({ row }) => {
                const score = (row.original as any)?.score.toFixed(4)
                return (
                    <div>{score}</div>
                )
            }
        },
        {
            id: "actions",
            enableHiding: false,
            cell: ({ row }) => {
                return (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0 hover:cursor-pointer">
                                <span className="sr-only">Open menu</span>
                                <MoreHorizontal />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuItem className='cursor-pointer' onClick={() => window.open(`/document/${row.original.id}?a=${account!.id}&p=${project!.id}`, "_blank")}>
                                Open Document
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                )
            }
        }
    ]
}

interface EmbeddingsDataTableProps {
    data: ContentObjectItem[]
    fullHeight?: boolean
}
export function EmbeddingsDataTable({ data, fullHeight = false }: Readonly<EmbeddingsDataTableProps>) {
    const { account, project } = useUserSession()

    const [sorting, setSorting] = useState<SortingState>([])
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
    const [treshold, setTreshold] = useState<number>(0.5)
    const [filteredData, setFilteredData] = useState<ContentObjectItem[]>(data.filter((item) => (item as any).score >= treshold))

    const table = useReactTable({
        data: filteredData,
        columns: columns(account, project),
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        state: {
            sorting,
            columnFilters,
            columnVisibility
        }
    })

    const handleTresholdUpdate = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value

        if (val === "") {
            setTreshold(0)
        } else {
            const num = parseFloat(val)
            if (isNaN(num)) {
                setTreshold(0)
            } else if (num < 0) {
                setTreshold(0)
            } else if (num > 1) {
                setTreshold(1)
            } else {
                setTreshold(num)
            }
        }
    }

    useEffect(() => {
        setFilteredData(data.filter((item) => (item as any).score >= treshold))
    }, [treshold, data])

    return (
        <div className="flex flex-col h-full w-full">
            <div className='flex flex-row gap-2 items-center py-2 mt-0'>
                <Slider value={[treshold]} onValueChange={(value) => setTreshold(value[0])} max={1} step={0.0001} />
                <Input min="0" max="1" step="0.0001" type='number' value={treshold} onChange={handleTresholdUpdate} className='border rounded-md p-2 w-fit' />
            </div>
            <div className={`flex flex-col h-full border rounded-md border-gray bg-white overflow-hidden ${fullHeight ? "min-h-[calc(100vh-210px)] max-h-[calc(100vh-210px)]" : "min-h-[calc(100vh-323px)] max-h-[calc(100vh-323px)]"}`}>
                <Table>
                    <TableHeader className="sticky top-0 bg-white z-10 after:absolute after:bottom-0 after:left-0 after:w-full after:h-px after:bg-gray-200">
                        {
                            table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {
                                        headerGroup.headers.map((header) => (
                                            <TableHead key={header.id}>
                                                {
                                                    header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )
                                                }
                                            </TableHead>
                                        ))
                                    }
                                </TableRow>
                            ))
                        }
                    </TableHeader>
                    <TableBody>
                        {
                            table.getRowModel().rows?.length ? (
                                table.getRowModel().rows.map((row) => (
                                    <TableRow
                                        key={row.id}
                                        data-state={row.getIsSelected() && "selected"}
                                    >
                                        {
                                            row.getVisibleCells().map((cell) => (
                                                <TableCell key={cell.id}>
                                                    {
                                                        flexRender(
                                                            cell.column.columnDef.cell,
                                                            cell.getContext()
                                                        )
                                                    }
                                                </TableCell>
                                            ))
                                        }
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length}
                                        className="h-24 text-center"
                                    >
                                        No results.
                                    </TableCell>
                                </TableRow>
                            )
                        }
                    </TableBody>
                </Table>
            </div>
            <div className="flex items-center justify-end space-x-2 pt-2 text-sm text-muted-foreground">
                {table.getFilteredRowModel().rows.length} element{table.getFilteredRowModel().rows.length <= 1 ? "" : "s"}.
            </div>
        </div>
    )
}